import React, { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html, Line } from '@react-three/drei';
import * as THREE from 'three';
import { formatPct } from '../lib/model';
import { BARS_BASE, BAR_STEP, STACK_TOP, barHeight, barX } from '../lib/layout';
import { TokenText } from '../ui/TokenText';
import { useLabelFactor } from './sceneHooks';
type Props = {
  candidates: {
    text: string;
    prob: number;
  }[];
  picking: number | null;
  lastX: number;
  reducedMotion: boolean;
};
const BAR_GEO = new THREE.BoxGeometry(0.9, 1, 0.9).translate(0, 0.5, 0);
const TOP_COLOR = new THREE.Color('#EDEEF2');
const REST_COLOR = new THREE.Color('#465070');
const PICK_COLOR = new THREE.Color('#FFFFFF');
const {
  damp
} = THREE.MathUtils;
export function PredictionBars({
  candidates,
  picking,
  lastX,
  reducedMotion
}: Props) {
  const labelFactor = useLabelFactor();
  const barRefs = useRef<(THREE.Mesh | null)[]>([]);
  const labelRefs = useRef<(THREE.Group | null)[]>([]);
  const matRefs = useRef<(THREE.MeshStandardMaterial | null)[]>([]);
  const count = candidates.length;
  useFrame((_, delta) => {
    const d = Math.min(delta, 0.05);
    candidates.forEach((c, k) => {
      const bar = barRefs.current[k];
      if (!bar) return;
      bar.scale.y = damp(bar.scale.y, barHeight(c.prob), reducedMotion ? 30 : 5, d);
      const label = labelRefs.current[k];
      if (label) label.position.y = BARS_BASE + bar.scale.y + 0.42;
      const mat = matRefs.current[k];
      if (mat) {
        const target = picking === k ? PICK_COLOR : k === 0 && picking === null ? TOP_COLOR : REST_COLOR;
        mat.color.lerp(target, 1 - Math.exp(-d * 8));
        mat.emissive.copy(mat.color);
        const glow = picking === k ? 0.9 : k === 0 && picking === null ? 0.3 : 0.06;
        mat.emissiveIntensity = damp(mat.emissiveIntensity, glow, 8, d);
      }
    });
  });
  return <group data-id="element-55" data-mp="cj50k6fwuv1g">
      <mesh position={[0, BARS_BASE - 0.02, 0]} raycast={() => null} data-id="element-56" data-mp="ciwp8izfrxsd">
        <boxGeometry args={[count * BAR_STEP + 0.5, 0.04, 1.5]} data-id="element-57" data-mp="7nzf18qf7idw" />
        <meshStandardMaterial color="#141722" roughness={0.8} data-id="element-58" data-mp="31eximrhsbryz" />
      </mesh>

      {candidates.map((c, k) => <Line key={`line-${k}`} points={[[lastX, STACK_TOP, 0], [barX(k, count), BARS_BASE, 0]]} color="#FFFFFF" lineWidth={1} transparent opacity={0.08 + 0.55 * c.prob} data-id="element-59" data-mp="35gizenxfwqeg" data-mpi={k} />)}

      {candidates.map((c, k) => {
      return <group key={`bar-${k}`} data-id="element-60" data-mp="3k6yl03trq0d5" data-mpi={k}>
            <mesh ref={el => {
          barRefs.current[k] = el;
        }} geometry={BAR_GEO} position={[barX(k, count), BARS_BASE, 0]} scale={[1, 0.0001, 1]} data-id="element-61" data-mp="3p2xhkep92oon">
              <meshStandardMaterial ref={el => {
            matRefs.current[k] = el;
          }} color={REST_COLOR} emissive={REST_COLOR} emissiveIntensity={0.06} roughness={0.4} data-id="element-62" data-mp="1y7t02sv406z" />
            </mesh>
            <group ref={el => {
          labelRefs.current[k] = el;
        }} position={[barX(k, count), BARS_BASE + 0.5, 0]} data-id="element-63" data-mp="1w8xw2y97sow7">
              <Html center distanceFactor={10 * labelFactor} zIndexRange={[10, 0]} wrapperClass="pointer-events-none" className="pointer-events-none" data-id="element-64" data-mp="1mhbbwcewffhb">
                <div className="select-none whitespace-nowrap text-center animate-[fadeIn_0.6s_ease_both]" data-id="element-65" data-mp="2a6vyd5a9guvc">
                  <TokenText text={c.text} className={`block text-[17px] font-semibold ${k === 0 || picking === k ? 'text-[#EDEEF2]' : 'text-[#9A9FB0]'}`} data-id="element-66" data-mp="256zztlkc1vn6" />
                  <div className="font-mono text-[13px] text-[#6B7084]" data-id="element-67" data-mp="tq9ue4nqh7um">{formatPct(c.prob)}</div>
                </div>
              </Html>
            </group>
          </group>;
    })}

      <Html center position={[0, BARS_BASE - 0.05, 0.95]} distanceFactor={10 * labelFactor} zIndexRange={[10, 0]} wrapperClass="pointer-events-none" className="pointer-events-none" data-id="element-68" data-mp="2dldy23unesxl">
        <span className="select-none whitespace-nowrap text-[12px] font-medium uppercase tracking-[0.08em] text-[#5D6272]" data-id="element-69" data-mp="28nukl65ryk6p">
          Top {count} of ~50,000 candidates
        </span>
      </Html>
    </group>;
}